import { usePrintVoucher } from 'api-hooks/general/mutation';
import { AuthorizationRules } from 'common/constants';
import { base64ToUrl } from 'common/utils/string';
import notification from 'components/common/notification';
import FileSaver from 'file-saver';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useHotelSaveVoucher(type, data) {
  const { t } = useTranslation();
  const { can } = useAuthorization();
  const { mutateAsync } = usePrintVoucher();
  const dialog = useDialog();

  const id = data.id;
  const invoiceNumber = data.invoiceNumber;

  const permission = React.useMemo(() => {
    switch (type) {
      case 'hotel-sales':
        return AuthorizationRules.HotelSalesPrintVoucher;
      default:
        return false as unknown as AuthorizationRules;
    }
  }, [type]);

  const saveVoucherPermission = can(permission);

  const onDownloadVoucher = React.useCallback(
    (result) => {
      try {
        const fileName = `Voucher_${invoiceNumber}`;
        FileSaver.saveAs(result as Blob, fileName);
      } catch (error) {
        notification.error({ message: error.message });
      }
    },
    [invoiceNumber],
  );

  const onPrintVoucher = React.useCallback((result) => {
    try {
      const base64string = result.split(',')[1];
      const url = base64ToUrl(base64string, 'application/pdf');

      const printWindow = window.open(url, '_blank');
      printWindow?.print();
    } catch (error) {
      notification.error({ message: error.message });
    }
  }, []);

  const onSaveHotelVoucher = React.useCallback(async () => {
    if (saveVoucherPermission) {
      dialog.showConfirmation({
        message: t('invoice:print_or_download_confirmation_text'),
        title: t('common:confirmation'),
        positiveLabel: t('common:print'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await mutateAsync({
              id,
              type,
            });

            onPrintVoucher(result);
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },
        negativeLabel: t('common:download'),
        onNegativeAction: async (onClose) => {
          try {
            const result = await mutateAsync({
              id,
              type,
            });

            onDownloadVoucher(result);
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },
      });
    } else {
      notification.error({
        message: t('common:unauthorized_action'),
      });
    }
  }, [
    dialog,
    id,
    mutateAsync,
    onDownloadVoucher,
    onPrintVoucher,
    saveVoucherPermission,
    t,
    type,
  ]);

  return {
    saveVoucherPermission,
    onSaveHotelVoucher,
  };
}
